import React, { useCallback, useEffect, useRef, useState } from "react";
import { GoogleMap } from "@react-google-maps/api";

import { Colors, defaultMapCenter, MAP_LIBRARIES } from "../Constants";

interface MapBounds {
  northEast: { lat: number; lng: number };
  southWest: { lat: number; lng: number };
}

interface mapCenterType {
  lat: number;
  lng: number;
}

interface markerType {
  id?: string;
  title?: string;
  price?: string;
  location: {
    lat: number;
    lng: number;
  };
}

type zoomRange = 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18;

interface MapViewComponentType {
  id: string;
  mapCenter?: mapCenterType;
  markers?: markerType[] | undefined;
  zoomLevel?: zoomRange;
  mapHeight?: number;
  onMarkerClicked?: Function;
}

const createTravelMarkerSVG = (rating: number, zoomLevel: number = 15) => {
  const markerColor = Colors.primary;

  const baseSize = Math.max(80, Math.min(40, rating * 8));

  // Scale based on zoom level - smaller when zoomed out, larger when zoomed in
  let zoomMultiplier = 1;
  if (zoomLevel <= 6) {
    zoomMultiplier = 0.4;
  } else if (zoomLevel <= 12) {
    zoomMultiplier = 1.0;
  } else {
    zoomMultiplier = 2.0;
  }

  const size = Math.max(16, baseSize * zoomMultiplier);

  const innerRadius = Math.max(3, (size / 2 - 8) * (0.4 + rating * 0.1));
  const outerRadius = Math.max(6, size / 2 - 3);

  const strokeWidth = Math.max(1, Math.min(3, size / 20));

  return `
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow-${size}" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="1" stdDeviation="${Math.max(
            1,
            size / 30
          )}" flood-color="rgba(0,0,0,0.3)"/>
        </filter>
      </defs>
      
      <!-- Outer uncertainty circle (semi-transparent) -->
      <circle 
        cx="${size / 2}" 
        cy="${size / 2}" 
        r="${outerRadius}" 
        fill="${markerColor}" 
        opacity="0.3"
        stroke="${markerColor}" 
        stroke-width="${strokeWidth}"
        stroke-dasharray="${Math.max(2, size / 15)},${Math.max(2, size / 15)}"
        filter="url(#shadow-${size})"
      >
        ${
          size > 30
            ? `<animateTransform 
          attributeName="transform" 
          type="scale" 
          values="1;1.05;1" 
          dur="3s" 
          repeatCount="indefinite"/>`
            : ""
        }
      </circle>
      
      <!-- Inner estimated location circle -->
      <circle 
        cx="${size / 2}" 
        cy="${size / 2}" 
        r="${innerRadius}" 
        fill="${markerColor}" 
        opacity="0.7"
        stroke="white" 
        stroke-width="${Math.max(1, strokeWidth - 1)}"
      />
    </svg>
  `;
};

const MapViewComponent: React.FC<MapViewComponentType> = ({
  id,
  mapCenter,
  markers = [],
  zoomLevel = 12,
  mapHeight = 230,
  onMarkerClicked = null,
}) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [bounds, setBounds] = useState<MapBounds | null>(null);
  const [zoom, setZoom] = useState<number>(zoomLevel);

  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);

  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map);

    const initialBounds = map.getBounds();
    if (initialBounds) {
      const ne = initialBounds.getNorthEast();
      const sw = initialBounds.getSouthWest();
      setBounds({
        northEast: { lat: ne.lat(), lng: ne.lng() },
        southWest: { lat: sw.lat(), lng: sw.lng() },
      });
    }
  }, []);

  const onUnmount = () => {
    markersRef.current.forEach((marker) => {
      marker.map = null;
    });

    markersRef.current = [];

    setMap(null);
  };

  const onBoundsChanged = useCallback(() => {
    if (map) {
      const newBounds = map.getBounds();
      const newZoom = map.getZoom();

      if (newBounds && newZoom) {
        const ne = newBounds.getNorthEast();
        const sw = newBounds.getSouthWest();

        setBounds({
          northEast: { lat: ne.lat(), lng: ne.lng() },
          southWest: { lat: sw.lat(), lng: sw.lng() },
        });
        setZoom(newZoom);
      }
    }
  }, [map]);

  const createAdvancedMarker = useCallback(
    (
      position: google.maps.LatLngLiteral,
      content: HTMLElement,
      title: string,
      onClick?: () => void
    ) => {
      if (!map) return null;

      const marker = new google.maps.marker.AdvancedMarkerElement({
        map,
        position,
        content,
        title,
      });

      if (onClick) {
        marker.addListener("click", onClick);
      }

      return marker;
    },
    [map]
  );

  const createMarkerContent = useCallback(
    (rating: number, currentZoom: number) => {
      const svgString = createTravelMarkerSVG(rating, currentZoom);
      const div = document.createElement("div");
      div.innerHTML = svgString;
      return div;
    },
    []
  );

  const clearMarkers = useCallback(() => {
    markersRef.current.forEach((marker) => {
      marker.map = null;
    });
    markersRef.current = [];
  }, []);

  useEffect(() => {
    if (map && map.current && mapCenter) {
      map.current.panTo(mapCenter); // smoothly move
      // or
      // map.current.setCenter(mapCenter); // jump immediately
    }
  }, [mapCenter]);

  useEffect(() => {
    if (map && markers.length > 0) {
      clearMarkers();

      for (let i = 0; i < markers.length; i++) {
        const markerContent = createMarkerContent(1, zoom);

        const marker = createAdvancedMarker(
          markers[i].location,
          markerContent,
          markers[i].title,
          () => {
            if (onMarkerClicked) {
              onMarkerClicked(markers[i].item);
            }
          }
        );

        if (marker) {
          markersRef.current.push(marker);
        }
      }
    }
    return () => {
      clearMarkers();
    };
  }, [map, clearMarkers, markers]);

  const containerStyle = {
    width: "100%",
    height: mapHeight,
  };

  return (
    <div className="map-wrapper">
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={mapCenter || defaultMapCenter}
        zoom={zoom || zoomLevel}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onBoundsChanged={onBoundsChanged}
        options={{
          zoomControl: true,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          rotateControl: false,
          scaleControl: true,
          clickableIcons: false,
          minZoom: 3,
          maxZoom: 18,
          mapId: id || "spacii-google-map",

          disableDefaultUI: true,
        }}
      />
    </div>
  );
};

export default MapViewComponent;
